import { withSuperJSONPage as _withSuperJSONPage } from "babel-plugin-superjson-next/tools";
import { withSuperJSONProps as _withSuperJSONProps } from "babel-plugin-superjson-next/tools";
import type { ParsedUrlQuery } from 'querystring';
import * as React from 'react';
import { Prisma } from '@prisma/client';
import { GetStaticProps, GetStaticPaths, NextPage } from 'next';
import { NextSeo } from 'next-seo';
import { WorkoutView } from '@/components/workouts';
import prisma from '@/lib/prisma';
import { decay } from '@/utils/sort';
import { RouterOutput } from '../../trpc/router';
interface WorkoutPageParams extends ParsedUrlQuery {
  slug: string;
}
interface WorkoutPageProps {
  workout: RouterOutput['workouts']['byId']['data'];
  moreWorkouts: RouterOutput['workouts']['all'];
}

const WorkoutPage: NextPage<WorkoutPageProps> = ({
  workout,
  moreWorkouts
}) => {
  const metaImages = workout.image ? [{
    url: `/${workout.image.fileName}`,
    height: workout.image.height,
    width: workout.image.width,
    alt: workout.image.title
  }] : undefined;
  return <>
      <NextSeo description={workout.content ?? undefined} openGraph={{
      images: metaImages
    }} title={workout.title} />
      <WorkoutView moreWorkouts={moreWorkouts} workout={workout} />
    </>;
};

export const getStaticPaths: GetStaticPaths<WorkoutPageParams> = async () => {
  const workouts = await prisma.workout.findMany({
    take: 100,
    select: {
      slug: true
    }
  });
  const paths = workouts.map(w => ({
    params: {
      slug: w.slug
    }
  }));
  return {
    paths,
    fallback: 'blocking'
  };
};
export const getStaticProps: GetStaticProps<WorkoutPageProps, WorkoutPageParams> = _withSuperJSONProps(async req => {
  const slug = (req.params?.slug as string);
  const workout = (await prisma.workout.findUniqueOrThrow({
    where: {
      slug
    },
    include: {
      phasedays: {
        select: {
          phaseday: {
            select: {
              id: true,
              number: true,
              phase: {
                include: {
                  days: {
                    select: {
                      number: true
                    }
                  }
                }
              }
            }
          }
        }
      },
      image: true,
      video: true,
      instructor: {
        include: {
          photo: true,
          _count: true
        }
      }
    } //TODO: Cleanup after removing playbacks from video proc

  }) as RouterOutput['workouts']['byId']['data']);

  if (!workout || !workout.phasedays) {
    return {
      notFound: true
    };
  }

  const phasedayNumbers = workout.phasedays.flatMap(({
    phaseday
  }) => phaseday.phase.days.map(day => day.number));
  const moreWorkouts = await prisma.workout.findMany({
    where: {
      published: true,
      id: {
        notIn: [workout.id]
      },
      phasedays: {
        some: {
          phaseday: {
            number: {
              in: [...new Set(phasedayNumbers)]
            }
          }
        }
      }
    },
    orderBy: {
      createdAt: Prisma.SortOrder.desc
    },
    take: 12,
    include: {
      phasedays: {
        select: {
          phaseday: {
            select: {
              id: true,
              number: true,
              phase: {
                include: {
                  days: {
                    select: {
                      number: true
                    }
                  }
                }
              }
            }
          }
        }
      },
      image: true,
      video: true,
      instructor: {
        include: {
          photo: true,
          _count: true
        }
      }
    }
  }).then(workouts => workouts.sort((a, b) => {
    if (decay(a.views, a.createdAt) < decay(b.views, b.createdAt)) return 1;
    if (decay(a.views, a.createdAt) > decay(b.views, b.createdAt)) return -1;
    return 0;
  }));
  return {
    props: {
      workout: { ...workout,
        isLocked: false
      },
      moreWorkouts: moreWorkouts.sort(() => 0.5 - Math.random()).map(wo => ({ ...wo,
        isLocked: false
      }))
    }
  };
}, []);
export default _withSuperJSONPage(WorkoutPage);